import styled, {css} from "styled-components";
import {ListItemButton} from "@mui/material";
import type {Theme} from "@mui/material/styles";
import {brand, goldEdgeFor, radii} from "theme/tokens";
import {eyebrowTextCss} from "theme/typography";
import {
	type CollapsibleNavItem,
	menuRowHiddenCss,
	navCollapseSectionHiddenCss,
} from "nav/navCollapse";

/**
 * Externalized styles for {@link ProfileMenu} (owner externalization mandate, 2026-07-12): the
 * component file keeps markup + behavior only, the styled-components + MUI `sx` builders live here
 * and read `theme.tokens` exclusively — never a hardcoded color.
 */

/**
 * v3 avatar treatment (ticket #28): initials use the mode-aware ink paired with the blue surface,
 * preserving contrast when dark mode brightens that surface.
 */
export const avatarSx = (size: number) => (theme: Theme) => ({
	bgcolor: theme.tokens.blue,
	color: theme.tokens.blueInk,
	fontWeight: 700,
	fontSize: `${Math.round(size * 0.36)}px`,
	width: size,
	height: size,
});

/** Focus ring for the rail's avatar trigger, matching the rail controls' focus treatment. The
 *  trigger is also the {@link PastDueBadge}'s positioning context, hence the explicit `position`. */
export const triggerSx = (theme: Theme) => ({
	position: "relative",
	"&:focus-visible": {
		outline: `2px solid ${theme.tokens.focusOutline}`,
		outlineOffset: "2px",
	},
});

/**
 * The payment-problem dot on the avatar trigger (ui#1778) — the draw-attention half of the warning
 * that lives inside the menu as {@link PastDueNotice}. A plain dot, deliberately without pulse or
 * count, ringed in the page surface so it reads on top of the avatar's blue in both modes. It is
 * decoration for sighted users: `aria-hidden`, no accessible name — the notice in the menu is the
 * accessible surface, which is also why the e2e spec locates this by test id rather than by role.
 */
/**
 * The trigger avatar's initials, drawn from `data-initials` as generated content so they decorate the button
 * instead of being its text: axe-core 4.14's `label-content-name-mismatch` (WCAG 2.5.3) read the trigger's visible
 * text as the initials, which its name "Account menu" does not contain. The cost, accepted on 2026-10-29
 * (kitecorp/transferify-ui#3842): a speech-input user cannot say the initials to press it; its name still works.
 * Rendered inside the MUI Avatar as its children, so the Avatar still shows them when there is no picture.
 */
export const TriggerInitials = styled.span`
	&::before {
		content: attr(data-initials);
	}
`;

export const PastDueBadge = styled.span.attrs({"aria-hidden": true})`
	position: absolute;
	top: 3px;
	right: 3px;
	width: 11px;
	height: 11px;
	border-radius: 50%;
	background: ${({theme}) => theme.tokens.orange};
	box-shadow: 0 0 0 2px ${({theme}) => theme.tokens.paper};
	pointer-events: none;
`;

/**
 * The past-due payment notice, sitting directly under the identity row (ui#1778 — owner-approved
 * prototype). Same surface family the floating banner used: goldTint hairlined in orange — with the
 * tint composited over the opaque `card` this popover is made of (ui#1779's lesson: goldTint alone
 * is a low-alpha wash and lets whatever is behind it bleed through).
 */
export const PastDueNotice = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 8px;
	margin: 12px;
	padding: 10px 12px;
	border-radius: ${radii.control};
	border: 1px solid ${({theme}) => theme.tokens.orange};
	background: ${({theme}) =>
		`linear-gradient(${theme.tokens.goldTint}, ${theme.tokens.goldTint}) ${theme.tokens.card}`};
	color: ${({theme}) => theme.tokens.ink};
	font-size: 13px;
	line-height: 1.35;
	text-align: left;
`;

/** v3 card surface for the popover paper: card + cardBorder + shadowCard on radii.panel. */
export const paperSx = (theme: Theme) => ({
	// Gap below the top-bar avatar (the popover drops down from it, ui#96).
	mt: 1,
	width: 320,
	backgroundColor: theme.tokens.card,
	// Kill MUI's dark-mode elevation overlay so the surface is exactly tokens.card.
	backgroundImage: "none",
	border: `1px solid ${theme.tokens.cardBorder}`,
	borderRadius: radii.panel,
	boxShadow: theme.tokens.shadowCard,
	// Let the nested Language submenu's side flyout escape the popover box instead of being clipped (ui#119).
	overflow: "visible",
});

/** Neutral avatar-trigger wrapper — the top bar's pill owns spacing now (ui#96 dropped the rail's
 *  vertical `margin-bottom`). */
export const Root = styled.div`
	display: flex;
	align-items: center;
`;

export const IdentityRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 14px 16px;
`;

export const IdentityText = styled.div`
	flex: 1;
	min-width: 0;
`;

export const Name = styled.p`
	margin: 0;
	font-size: 14.5px;
	font-weight: 700;
	letter-spacing: -0.01em;
	color: ${({theme}) => theme.tokens.ink};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

/**
 * "Add your name" affordance for nameless accounts (ui#139) — link-styled in the Name slot, with a
 * 44px hit area kept visually dense via negative margins (mobile touch-target rule).
 */
export const AddNameButton = styled.button`
	background: none;
	border: none;
	align-self: flex-start;
	display: inline-flex;
	align-items: center;
	min-height: 44px;
	padding: 0 4px;
	margin: -14px 0 -14px -4px;
	font: inherit;
	font-size: 14px;
	font-weight: 700;
	color: ${({theme}) => (theme.tokens.mode === "dark" ? theme.tokens.segInk : theme.tokens.blue)};
	text-decoration: underline;
	text-underline-offset: 2px;
	cursor: pointer;

	&:hover {
		opacity: 0.8;
	}
`;

/** Inline name entry (ui#139): compact field in the identity row; Enter saves, Escape cancels. */
export const NameInput = styled.input`
	width: 100%;
	box-sizing: border-box;
	padding: 5px 8px;
	font: inherit;
	font-size: 14px;
	font-weight: 600;
	color: ${({theme}) => theme.tokens.ink};
	background: ${({theme}) => theme.tokens.field};
	border: 1px solid ${({theme}) => theme.tokens.line};
	border-radius: 8px;
	outline: none;

	&:focus {
		border-color: ${({theme}) => theme.tokens.focusOutline};
	}

	&:disabled {
		opacity: 0.6;
	}
`;

export const Secondary = styled.p`
	margin: 0;
	font-size: 12.5px;
	color: ${({theme}) => theme.tokens.ink2};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
`;

/**
 * Right-aligned column holding the plan indicator and, below Premium, the Upgrade pill beneath it
 * (ui#167). Stacking vertically (not side-by-side) keeps the identity row uncrowded on the fixed
 * 320px popover — the pill uses the row's spare vertical space instead of squeezing the name.
 */
export const PlanChips = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 5px;
	flex: none;
`;

/**
 * The tier chip and its trial qualifier on ONE line (ui#1441).
 *
 * Horizontal rather than another rung in {@link PlanChips}: the qualifier only means anything read
 * against the tier it qualifies ("BASIC · TRIAL"), and a third stacked rung would grow the identity
 * row past the avatar it is aligned to. Both children are `flex: none`, so the pair takes its natural
 * width and the name/email column beside it absorbs the difference through its own ellipsis.
 */
export const PlanChipRow = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

/**
 * The geometry every identity-row chip shares — one padding/radius/type ramp, so a second chip beside
 * the tier chip cannot drift half a pixel away from it. Only the FILL differs between them.
 */
const identityChipCss = css`
	${eyebrowTextCss};
	flex: none;
	padding: 4px 9px;
	border-radius: ${radii.pill};
`;

/** Plan chip: gold pro pill (brand.gold/.goldInk, design `.plan-badge`) or neutral Free chip. */
export const PlanChip = styled.span<{$pro: boolean}>`
	${identityChipCss};
	background-color: ${({theme, $pro}) => ($pro ? brand.gold : theme.tokens.chip)};
	color: ${({theme, $pro}) => ($pro ? brand.goldInk : theme.tokens.ink2)};
	/* Only the gold chip needs an edge; the neutral Free chip already separates from the menu card. */
	outline: ${({theme, $pro}) => ($pro ? `1px solid ${goldEdgeFor(theme.tokens)}` : "none")};
	outline-offset: -1px;
`;

/**
 * Trial qualifier shown beside the tier chip when a referral window — not a subscription — is what
 * answers the plan fields (kitecorp/transferify-api-service#693).
 *
 * Deliberately the repo's NEUTRAL chip idiom (`tokens.chip` on `tokens.ink2`, the same pairing as
 * {@link PlanChip}'s Free state and `reminders`' `StatusChip`) rather than a colour of its own. The
 * two colours already in this corner are spoken for and would both mislead: gold is the paid-tier
 * fill this chip exists to qualify, and blue is the CTA ink of the Upgrade pill directly beneath, so
 * a blue chip would read as a second thing to click. Neutral-beside-gold is the filled/quiet pair
 * that says "qualifier", and it carries real contrast in both modes without competing.
 */
export const TrialChip = styled.span`
	${identityChipCss};
	background-color: ${({theme}) => theme.tokens.chip};
	color: ${({theme}) => theme.tokens.ink2};
`;

/**
 * Subtle upgrade pill (ui#167, ui#189): a blue-tinted CTA sitting under the plan chip, naming the next
 * plan and opening its checkout exactly as the Upgrade menu row does (kitecorp/transferify-ui#3701).
 * Tinted (not solid) so it reads as a quiet upsell, inverting to solid blue on hover/focus.
 */
export const UpgradePill = styled.button`
	${eyebrowTextCss};
	position: relative;
	isolation: isolate;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	min-width: 44px;
	min-height: 44px;
	padding: 0 9px;
	margin: -10px 0;
	border: none;
	background: transparent;
	color: ${({theme}) => theme.tokens.blue};
	cursor: pointer;
	transition: color 0.16s;

	&::before {
		content: "";
		position: absolute;
		z-index: -1;
		inset: 10px 0;
		border-radius: ${radii.pill};
		background-color: ${({theme}) => theme.tokens.accentTint};
		transition: background-color 0.16s;
	}

	&:hover {
		color: ${brand.white};
	}

	&:hover::before {
		background-color: ${({theme}) => theme.tokens.blue};
	}

	&:focus-visible {
		outline: none;
	}

	&:focus-visible::before {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

export const UsageBlock = styled.div`
	padding: 12px 16px;
`;

/** JetBrains-Mono uppercase eyebrow label — the shared v3 eyebrow ramp from `theme/typography`. */
export const UsageLabel = styled.span`
	${eyebrowTextCss};
	display: block;
	color: ${({theme}) => theme.tokens.ink3};
`;

/** The usage figure: big bold number with tabular digits, so the byte count reads as one column. */
export const UsageValue = styled.p`
	margin: 6px 0 0;
	font-size: 21px;
	font-weight: 700;
	letter-spacing: -0.01em;
	font-variant-numeric: tabular-nums;
	color: ${({theme}) => theme.tokens.ink};
`;

export const UsageCaption = styled.p`
	margin: 4px 0 0;
	font-size: 12px;
	color: ${({theme}) => theme.tokens.ink3};
`;

export const MenuRow = styled(ListItemButton)`
	display: flex;
	align-items: center;
	gap: 10px;
	min-height: 44px;
	font-size: 13.5px;
	font-weight: 600;
	padding: 10px 16px;
	color: ${({theme}) => theme.tokens.ink};

	&:hover {
		background-color: ${({theme}) => theme.tokens.field};
	}
`;

/**
 * The leading glyph slot on every menu row (ui#520). A fixed 20px box rather than shrink-to-fit, so
 * the labels line up in one column whatever each glyph's own drawing width is.
 *
 * It sets NO colour: the glyphs stroke in `currentColor`, so each row's ink flows straight through —
 * which is what keeps the accented Upgrade row's icon blue with its label instead of needing a second
 * rule here that would have to be kept in step with {@link UpgradeRow}.
 *
 * The glyphs inside are `aria-hidden` (see `nav/pro/icons`), so this slot adds nothing to the row's
 * accessible name: the text remains the whole of it.
 */
export const RowIcon = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: 20px;
	height: 20px;
`;

/**
 * The Upgrade action row (ui#167, ui#189): the {@link MenuRow} idiom accented in brand blue and
 * bolder, so the conversion CTA reads as the primary action in the actions list.
 */
export const UpgradeRow = styled(MenuRow)`
	color: ${({theme}) => theme.tokens.blue};
	font-weight: 700;
`;

/**
 * The pill items the top bar hands over on a narrow viewport (ui#451), each shown under the EXACT
 * complement of the query that hides it in the pill (`pillOnlyNavCss` in `NavBar.styles`). Both come
 * from the one entry in `NAV_COLLAPSE_RANGE`, so an item is in the bar or in this menu — never in
 * both, and never in neither. Personalization's complement is two rules rather than one, because its
 * range is a band (ui#452); the module owns that derivation so the two sides cannot drift.
 */
export const CollapsedNavRow = styled(MenuRow)<{$collapse: CollapsibleNavItem}>`
	${({$collapse}) => css`
		${menuRowHiddenCss($collapse)}
	`};
`;

/**
 * Wrapper for the collapsed group and its divider: hidden at every width where the bar hands the menu
 * nothing, so no popover shows an empty section or an orphan rule. Since Reminders left the bar
 * (kitecorp/transferify-ui#3140) that is not only "above the widest threshold" but also 421–640px,
 * which is why the rule comes from `navCollapse` rather than from one width.
 */
export const CollapsedNavSection = styled.div`
	${navCollapseSectionHiddenCss()}
`;

